import React, { useMemo } from 'react';
import Header from '@amzn/awsui-components-core/header';
import Container from '@amzn/awsui-components-core/container';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Spinner from '@amzn/awsui-components-core/spinner';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import PercentageMetricCard from './PercentageMetricCard';
import VolumeCard from './VolumeCard';
import DataModeSelector from './DataModeSelector';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import {
  dataModeChanged,
  reportingWeekChanged,
  filterQueryChanged,
} from 'src/features/dashboard/dashboardSlice';
import {
  PRIMARY_FILTER_KEYS,
  HOME_FILTER_PROPERTIES,
  DEFAULT_FILTER_QUERY,
  activeFilterCount,
} from 'src/config/filterConfig';
import { FilterControlBar } from 'src/components/filters/controls/FilterControlBar';
import { useMetricsWindow } from 'src/features/api/useMetricsWindow';
import { useGetMetricDefinitionsQuery } from 'src/features/api/metricDefinitionsApi';
import { useGetWeekNumbersQuery } from 'src/features/api/weekNumbersApi';
import {
  availableAnchorWeeks,
  resolveWindow,
  toCanonicalWeekToken,
} from 'src/features/api/durationRange';
import { DataMode, WBR_METRICS_ONLY_PRESET } from 'src/types/metricsApi.types';
import { selectWbrDashboardData } from 'src/features/api/metricsSelectors';
import {
  buildDcrPredicate,
} from 'src/features/api/filterPredicate';
import { WBR_REVIEW_TYPE } from 'src/constants';
import { useActiveDashboard } from 'src/features/api/useActiveDashboard';
import { ComingSoon } from 'src/components/common/ComingSoon';

const WbrMetricsDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const mode = useAppSelector((s) => s.dashboard.dataMode);
  const filterQuery = useAppSelector((s) => s.dashboard.filterQuery);
  // The ONE reporting week shared app-wide (spec domains-page-week-picker
  // R3.1) — the same state every page's header week picker writes, so a
  // pick made anywhere carries here and vice versa.
  // This page's window ANCHORS on it (R3.3): the cards show the picked week,
  // with the 4 weeks before it as the trend/delta history. Weeks are cached
  // individually, so shifting the anchor only fetches the weeks it gains.
  const reportingWeek = useAppSelector((s) => s.dashboard.reportingWeek);
  // The served dashboard being viewed (multi-dashboard R3.1) — joins every
  // data call; caches key on it, so switching never refetches the other one.
  // The heading names the view only ("Executive Summary") — identity lives in
  // the header switcher; comingSoon swaps the body for the onboarding
  // placeholder (its label supplies the coming-soon aria-label).
  const activeDashboard = useActiveDashboard();
  const dashboardId = activeDashboard.dashboardId;
  // A dashboard being onboarded (structure.comingSoon) renders the
  // placeholder body and SKIPS every data fetch — the placeholder needs
  // nothing from the backend.
  const comingSoon = activeDashboard.structure.comingSoon === true;

  // The metric catalog (names, goals, groups, operand identity) — one fetch
  // per session, in PARALLEL with the metrics; joined by metricKey below.
  const {
    data: definitionsResponse,
    isLoading: defsLoading,
    isError: defsError,
    refetch: refetchDefinitions,
  } = useGetMetricDefinitionsQuery(
    { dashboardId, reviewType: WBR_REVIEW_TYPE },
    { skip: comingSoon },
  );

  // Week identity comes from the service (it owns the reporting calendar,
  // including year boundaries), so naming this page's weeks depends on the
  // universe. One small GET, cached for the session.
  const {
    data: weekData,
    isError: weeksError,
    refetch: refetchWeeks,
  } = useGetWeekNumbersQuery();

  // Weeks are cached individually, so overlapping windows across pages are
  // ordinary cache hits and shifting a window fetches only the weeks it gains
  // (spec metrics-window-caching R1). The neighbours of this window are warmed
  // once it resolves, making a nearby week switch instant (R5).
  // THE window — the SAME resolveWindow the Detailed View builds from (last 4
  // before the anchor + mode-derived forward weeks), so the trends show up to
  // 4 forecast weeks after the selected reporting week and the two pages'
  // cache keys match exactly: drilling in is all cache hits, no extra warm.
  const isSnapshot = mode === 'snapshot';
  const durationWindow = useMemo(
    () =>
      weekData
        ? resolveWindow(
            weekData.weeks,
            weekData.currentWeek,
            reportingWeek,
            isSnapshot,
          )
        : null,
    [weekData, isSnapshot, reportingWeek],
  );
  const weeks = durationWindow?.durationRange;
  // The same page in the other mode, warmed so the Live/Snapshot toggle does
  // not reload. The two windows differ: a snapshot ends at the last completed
  // week, because the running week has no capture.
  const otherModeWeeks = useMemo(
    () =>
      weekData
        ? resolveWindow(
            weekData.weeks,
            weekData.currentWeek,
            reportingWeek,
            !isSnapshot,
          )?.durationRange
        : undefined,
    [weekData, isSnapshot, reportingWeek],
  );

  // Only the mode toggle gets warmed (one click, whole page). No neighbour-
  // week warming — a week switch is an explicit dropdown action, and shifting
  // the anchored window fetches only the weeks it gains (weeks are cached
  // individually).
  const alsoWarm = useMemo(() => {
    const targets: Array<{ dataMode: DataMode; weeks: string[] }> = [];
    if (otherModeWeeks) {
      targets.push({
        dataMode: isSnapshot ? 'LIVE' : 'SNAPSHOT',
        weeks: otherModeWeeks,
      });
    }
    return targets;
  }, [otherModeWeeks, isSnapshot]);

  const {
    data: apiResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useMetricsWindow({
    id: dashboardId,
    dataMode: isSnapshot ? 'SNAPSHOT' : 'LIVE',
    // undefined = unresolved window → no week subscriptions, no warms: the
    // coming-soon placeholder must not fan out getMetrics calls.
    weeks: comingSoon ? undefined : weeks,
    alsoWarm: comingSoon ? undefined : alsoWarm,
  });

  const dashboardData = useMemo(
    () =>
      selectWbrDashboardData(
        apiResponse,
        definitionsResponse?.definitions,
        buildDcrPredicate(filterQuery),
        // No Metric Group/Name selections here: the card set is fixed (the
        // WBR preset below); those filters act only on the catalog pages.
        undefined,
        WBR_METRICS_ONLY_PRESET,
        undefined,
      ),
    [apiResponse, definitionsResponse, filterQuery],
  );

  // Dropdown options: every week the mode can anchor on (same rule and same
  // shared state as the Customer Domain page's picker), newest first — NOT
  // just the loaded window, so the user can jump anywhere from here.
  const availableWeeks = useMemo(
    () =>
      weekData
        ? availableAnchorWeeks(weekData.weeks, weekData.currentWeek, isSnapshot)
        : [],
    [weekData, isSnapshot],
  );

  // Which loaded week the cards headline: the window's ANCHOR (the resolved
  // reporting week, mode-clamped by resolveAnchor) — no longer the last week,
  // because the window now extends past the anchor into forecast weeks that
  // render only in the trend. Nothing dispatches here: an invalid pick clamps
  // at DISPLAY/FETCH level only, so visiting Home never erases a week chosen
  // on another page (spec domains-page-week-picker R3.1). If the anchor week
  // returned no rows (sparse data), fall back to the latest NON-FORECAST week
  // that did — a forecast must never masquerade as the headline actual.
  const anchorToken = durationWindow
    ? toCanonicalWeekToken(durationWindow.anchor)
    : null;
  const weekIndex =
    dashboardData && anchorToken
      ? dashboardData.weeks.indexOf(anchorToken)
      : -1;
  const lastNonForwardIndex = dashboardData
    ? dashboardData.weekTimelines.reduce(
        (last, timeline, i) => (timeline !== 'FUTURE' ? i : last),
        dashboardData.weeks.length - 1,
      )
    : 0;
  const resolvedIndex = weekIndex >= 0 ? weekIndex : lastNonForwardIndex;

  // The dropdown mirrors the same resolution: the pick when the mode can
  // anchor on it, else the resolved default the cards actually show.
  // Canonicalize first — a persisted raw reportingWeek anchors the data fine
  // but would miss this canonical-token includes() and show the default.
  const canonicalReporting = reportingWeek
    ? toCanonicalWeekToken(reportingWeek)
    : null;
  const selectedWeekForPicker =
    canonicalReporting && availableWeeks.includes(canonicalReporting)
      ? canonicalReporting
      : anchorToken;

  // The page shell (header, mode/week controls, filter bar) renders in EVERY
  // state — only the content area below it switches between spinner, error,
  // empty and cards. The controls are how the user escapes an empty state
  // (e.g. snapshot has no captures but live has data), so an empty state that
  // hides them is a trap.
  const content = (() => {
    // A failed week-universe fetch (getWeekNumbers) leaves `weeks` undefined,
    // which reads as "unresolved → loading" forever. A failed catalog fetch
    // (getMetricDefinitions) leaves selectWbrDashboardData returning null, which
    // reads as the "No metrics data" empty state and hides the real cause.
    // Surface either as an error so the user gets Retry instead of an endless
    // spinner or a misleading empty state.
    if (error || weeksError || defsError) {
      return (
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <SpaceBetween size="s" direction="vertical" alignItems="center">
            <Box color="text-status-error" fontWeight="bold">
              Failed to load metrics
            </Box>
            <Button
              onClick={() => {
                if (weeksError) refetchWeeks();
                if (defsError) refetchDefinitions();
                refetch();
              }}
            >
              Retry
            </Button>
          </SpaceBetween>
        </Box>
      );
    }
    // Gate on the catalog query too: selectWbrDashboardData returns null while
    // definitions are unresolved, so if the catalog resolves slower than the
    // metrics window (they fetch in parallel) the page would flash the "No
    // metrics data" empty state before the catalog arrives (as DeepDiveTab guards).
    if (isLoading || defsLoading) {
      return (
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <Spinner size="large" />
        </Box>
      );
    }
    // Loaded but no rows (e.g. backend cache not yet populated for this
    // dashboard/date range) — an explicit empty state, NOT the spinner.
    if (!dashboardData) {
      return (
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <SpaceBetween size="s" direction="vertical" alignItems="center">
            <Box fontWeight="bold">No metrics data available</Box>
            <Box color="text-body-secondary">
              The backend returned no rows for this dashboard in {mode} mode for
              this date range.
            </Box>
            <Button onClick={refetch}>Refresh</Button>
          </SpaceBetween>
        </Box>
      );
    }
    // Zero cards — either the active filters excluded every metric, or the
    // window genuinely has none. A NAMED empty state with a reset, never a
    // blank area under the filter bar (parity with MetricTableSection /
    // DeepDiveTab, spec R6.3/R6.4). (The preset scope degrades to "show all"
    // when the catalog declares no preset, so a missing preset never lands
    // here as an empty page.)
    if (
      dashboardData.percentageMetrics.length === 0 &&
      dashboardData.volumeMetrics.length === 0
    ) {
      const added = activeFilterCount(filterQuery);
      return (
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <SpaceBetween size="s" direction="vertical" alignItems="center">
            <Box fontWeight="bold">No metrics match the active filters</Box>
            <Box color="text-body-secondary">
              {added > 0
                ? 'The current filters exclude every metric for this window.'
                : 'No metrics for this window.'}
            </Box>
            {added > 0 && (
              <Button
                onClick={() =>
                  dispatch(filterQueryChanged(DEFAULT_FILTER_QUERY))
                }
              >
                Clear filters
              </Button>
            )}
          </SpaceBetween>
        </Box>
      );
    }
    return (
      <>
        {/* Top: all percentage metrics (generic — any % metric from API lands here) */}
        {dashboardData.percentageMetrics.length > 0 && (
          <Container
            header={<Header variant="h2">Delivery Performance</Header>}
          >
            <div className="otd-card-row">
              {dashboardData.percentageMetrics.map((m) => (
                <PercentageMetricCard
                  key={m.metricName}
                  metricName={m.metricName}
                  owner={m.owner}
                  goal={m.goal}
                  weeklyValues={m.weeklyValues}
                  weekIndex={resolvedIndex}
                  weeks={dashboardData.weeks}
                />
              ))}
            </div>
          </Container>
        )}

        {/* Bottom: all volume/count metrics (generic — any volume metric lands here) */}
        {dashboardData.volumeMetrics.length > 0 && (
          <Container header={<Header variant="h2">Volume Metrics</Header>}>
            <div className="volume-card-grid">
              {dashboardData.volumeMetrics.map((m) => (
                <VolumeCard
                  key={m.metricName}
                  metricName={m.metricName}
                  owner={m.owner}
                  goal={m.goal}
                  goalDirection={m.goalDirection}
                  unit={m.unit}
                  weeklyVolumes={m.weeklyVolumes}
                  weeklyAttainment={m.weeklyAttainment}
                  weekIndex={resolvedIndex}
                  weeks={dashboardData.weeks}
                />
              ))}
            </div>
          </Container>
        )}
      </>
    );
  })();

  return (
    <div className="home-page">
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            comingSoon ? undefined : (
              // Navigation lives in the page-tabs strip (spec
              // navigation-model R1.1) — no "View details" button here.
              <DataModeSelector
                mode={mode}
                selectedWeek={selectedWeekForPicker}
                availableWeeks={availableWeeks}
                onModeChange={(m) => dispatch(dataModeChanged(m))}
                onWeekChange={(w) => dispatch(reportingWeekChanged(w))}
              />
            )
          }
        >
          Executive Summary
          {!comingSoon && isFetching && !isLoading && <Spinner size="normal" />}
        </Header>

        {comingSoon ? (
          <ComingSoon label={activeDashboard.label} />
        ) : (
          <>
            <FilterControlBar
              properties={HOME_FILTER_PROPERTIES}
              primaryKeys={PRIMARY_FILTER_KEYS.summary}
              weeks={weeks}
            />
            {content}
          </>
        )}
      </SpaceBetween>
    </div>
  );
};

export default WbrMetricsDashboard;
